import { Icon, IconName, IconSize, IconSizes } from "@/components/icons/Icon";
import { TooltipV2, type TooltipPlacement } from "@/components/TooltipV2";
import { Loader2 } from "@/components/icons/lucideIcons";
import { AmplitudeEvent, useAmplitudeOnClick } from "@/utils/amplitude";
import { classNames } from "@/utils/classnames";
import { ChalkLink } from "@/components/interactive/ChalkLink";
import React, { ForwardedRef, forwardRef } from "react";

// ------------------------------------------------------------------
// ButtonV2 — dashboard button. Text + (optional) icon.
// Variant constraints:
//   - primary      solid green gradient, white text
//   - secondary    neutral surface with visible stroke
//   - tertiary     transparent ghost with subtle hover surface
//   - destructive  solid red
// ------------------------------------------------------------------

export const ButtonV2Variants = ["primary", "secondary", "tertiary", "destructive"] as const;
export type ButtonV2Variant = (typeof ButtonV2Variants)[number];

export const ButtonV2Sizes = ["xs", "sm", "md", "lg"] as const;
export type ButtonV2Size = (typeof ButtonV2Sizes)[number];
export const IconButtonV2Sizes = ["3xs", "2xs", ...ButtonV2Sizes] as const;
export type IconButtonV2Size = (typeof IconButtonV2Sizes)[number];
export const ButtonV2IconPlacements = ["left", "right"] as const;
export type ButtonV2IconPlacement = (typeof ButtonV2IconPlacements)[number];

// Per design spec — height / horizontal padding / gap:
//   xs: 24 / 6 / 6   sm: 28 / 8 / 6   md: 32 / 10 / 6   lg: 36 / 12 / 6
// A contained CountBadgeV2 reduces the right inset by 2px at every size.
// All sizes share the cds-6 (6px) corner radius.
const sizeClasses: Record<ButtonV2Size, string> = {
  xs: "h-6 px-cds-6 gap-cds-6 [&:has([data-count-badge-v2])]:pr-cds-4 text-xs rounded-cds-6",
  sm: "h-7 px-cds-8 gap-cds-6 [&:has([data-count-badge-v2])]:pr-cds-6 text-xs rounded-cds-6",
  md: "h-8 px-cds-10 gap-cds-6 [&:has([data-count-badge-v2])]:pr-cds-8 text-cds-sm rounded-cds-6",
  lg: "h-9 px-cds-12 gap-cds-6 [&:has([data-count-badge-v2])]:pr-cds-10 text-cds-sm rounded-cds-6",
};

const iconOnlyButtonSizeClasses: Record<ButtonV2Size, string> = {
  xs: "h-6 w-6 shrink-0 rounded-cds-6",
  sm: "h-7 w-7 shrink-0 rounded-cds-6",
  md: "h-8 w-8 shrink-0 rounded-cds-6",
  lg: "h-9 w-9 shrink-0 rounded-cds-6",
};

// Maps each ButtonV2 size to an existing `IconSize`. `sm` takes 12px
// rather than its previous ad-hoc 14px (within tolerance and avoids
// introducing a button-specific size to the shared Icon component).
const buttonIconSize: Record<ButtonV2Size, IconSize> = {
  xs: "sm", // 12px inside a 24px square
  sm: "sm", // 12px inside a 28px square
  md: "md", // 16px inside a 32px square
  lg: "md", // 16px inside a 36px square
};

// Light-mode color classes go through `cds-*` design-system tokens rather
// than `bg-white` / `bg-zinc-*` / `border-zinc-*`. `styles/dark-mode.css`
// has `!important` global overrides keyed on those Tailwind class names
// (e.g. `.dark .bg-white → zinc-900 !important`) that would otherwise
// flip the light-mode button whenever any ancestor carries `.dark`.
//
// Similarly `border-[1px]` is used instead of `border` because
// dark-mode.css has `.dark .border { border-color: zinc-700 }` which
// would repaint *any* border on a `.border` element in dark contexts.
// `border-[1px]` sets width-only and doesn't trigger that selector
// (border-style falls back to Tailwind's preflight `solid`).
const variantClasses: Record<ButtonV2Variant, string> = {
  primary: classNames(
    "text-white border-[1px]",
    // Primary uses the same fill + stroke tokens in light and dark
    // modes — the DS collapsed the (formerly) dark variants after
    // finding them visually indistinguishable.
    "bg-cds-primary-fill-default border-cds-primary-stroke-default",
    "hover:bg-cds-primary-fill-hover hover:border-cds-primary-stroke-hover",
    "aria-expanded:bg-cds-primary-fill-hover aria-expanded:border-cds-primary-stroke-hover",
    "active:bg-cds-primary-fill-active",
    // Elevation: shadow-cds-s on default/hover, inset on active (light only).
    // Dark mode strips shadows; focus (from baseClasses) still shows in dark.
    "shadow-cds-s active:shadow-cds-inset",
    "dark:shadow-none dark:active:shadow-none",
    // Disabled fill + border — identical treatment to secondary per spec.
    "disabled:!bg-cds-surface-active dark:disabled:!bg-white/[0.04]",
    "disabled:!border-cds-surface-border dark:disabled:!border-white/10",
  ),
  secondary: classNames(
    "bg-cds-surface-default text-cds-ink border-[1px] border-cds-surface-border",
    "hover:bg-cds-surface-hover",
    "aria-expanded:bg-cds-surface-hover",
    "active:bg-cds-surface-active",
    // Dark — opaque surfaces preserve the old translucent-white feel
    // without letting scrolled content or patterned canvases bleed through.
    "dark:bg-cds-raised-dark dark:text-zinc-100 dark:border-white/10",
    "dark:hover:bg-zinc-800 dark:hover:border-white/[0.16]",
    "dark:aria-expanded:bg-zinc-800 dark:aria-expanded:border-white/[0.16]",
    "dark:active:bg-zinc-800 dark:active:border-white/10",
    // Icon palette — light mode mirrors primary (icons inherit the
    // button's text color, so zinc-800 default / zinc-400 disabled
    // with no explicit SVG override). Dark mode dims icons one zinc
    // step off the label (zinc-100 text → zinc-200 icon / zinc-100
    // disabled). `!` beats dark-mode.css's `.dark svg { color:
    // inherit }`.
    "dark:[&_svg]:!text-zinc-200",
    "dark:disabled:[&_svg]:!text-zinc-100",
    "shadow-cds-s active:shadow-cds-inset",
    "dark:shadow-none dark:active:shadow-none",
    "disabled:!bg-cds-surface-active dark:disabled:!bg-white/[0.04]",
    "disabled:!border-cds-surface-border dark:disabled:!border-white/10",
  ),
  tertiary: classNames(
    "bg-transparent text-cds-ink-subtle border-[1px] border-transparent",
    "hover:bg-cds-surface-border hover:text-cds-ink-strong",
    "aria-expanded:bg-cds-surface-border aria-expanded:text-cds-ink-strong",
    "active:bg-cds-surface-border active:text-cds-ink-strong",
    "[&_svg]:!text-cds-ink-subtle",
    "[&:hover_svg]:!text-cds-ink-strong aria-expanded:[&_svg]:!text-cds-ink-strong [&:active_svg]:!text-cds-ink-strong",
    "dark:bg-transparent dark:text-zinc-400",
    "dark:hover:bg-white/[0.04]",
    "dark:hover:text-zinc-50",
    "dark:aria-expanded:bg-white/[0.04] dark:aria-expanded:text-zinc-50",
    "dark:active:bg-white/[0.08]",
    "dark:active:text-zinc-50",
    "dark:[&_svg]:!text-zinc-400",
    "dark:[&:hover_svg]:!text-zinc-50 dark:aria-expanded:[&_svg]:!text-zinc-50 dark:[&:active_svg]:!text-zinc-50",
    "dark:disabled:[&_svg]:!text-zinc-100",
    "disabled:!bg-transparent dark:disabled:!bg-transparent",
    "disabled:!border-transparent dark:disabled:!border-transparent",
    "disabled:!text-cds-ink",
    "disabled:opacity-50",
  ),
  destructive: classNames(
    "bg-red-500 text-white border-[1px] border-transparent",
    "hover:bg-red-600",
    "aria-expanded:bg-red-600",
    "active:bg-red-700",
    "dark:bg-red-500 dark:hover:bg-red-600 dark:aria-expanded:bg-red-600 dark:active:bg-red-700",
    // Destructive keeps shadow-cds-s across all interactive states (no inset).
    "shadow-cds-s dark:shadow-none",
    "disabled:!bg-cds-surface-active dark:disabled:!bg-white/[0.04]",
    "disabled:!border-cds-surface-border dark:disabled:!border-white/10",
  ),
};

// Shared disabled styling — text, opacity, and a gradient-clearing
// background-image reset. Intentionally does NOT set disabled
// background-color or border-color; each variant owns those so variants
// with an unusual default (e.g. IconButtonV2's tertiary) can opt out to
// transparent without losing a Tailwind-internal source-order tie-break
// against a shared rule. `!` (important) guarantees these beat variant
// hover/active rules.
const disabledClasses = classNames(
  "disabled:!bg-none",
  "disabled:!text-cds-ink-muted dark:disabled:!text-zinc-100",
  "dark:disabled:opacity-50",
);

const baseClasses = classNames(
  "inline-flex items-center justify-center font-medium whitespace-nowrap select-none",
  // Only color-family properties fade; box-shadow, gradient fills, and
  // borders snap instantly. Animating primary's gradient produces a
  // visible "uncertainty" mid-frame since background-image can't
  // interpolate — snapping it is the more confident UI.
  "transition-colors duration-75",
  // Focus uses the cds-focus composite box-shadow (green ring + base
  // shadow). `focus-visible:` keeps mouse clicks visually quiet while
  // preserving keyboard focus visibility.
  "focus:outline-none focus-visible:shadow-cds-focus",
  // `disabled:pointer-events-none` is intentionally NOT set — it would
  // suppress `cursor-not-allowed` because the cursor style needs the
  // element to stay pointer-interactive. HTML's `disabled` attribute
  // blocks click handlers regardless.
  "disabled:cursor-not-allowed",
  // No shadow on disabled buttons in either theme (! beats variant shadows).
  "disabled:!shadow-none",
  disabledClasses,
);

interface SharedProps {
  "data-testid"?: string;
  variant?: ButtonV2Variant;
  size?: ButtonV2Size;
  loading?: boolean;
  // Icon is named (not JSX). The component renders it internally via
  // `<Icon name={...} />` and controls size / color / animation
  // centrally — rather than accepting an arbitrary ReactNode. See
  // `components/icons/Icon.tsx` for the IconName catalog.
  icon?: IconName;
  iconPlacement?: ButtonV2IconPlacement;
  /** @deprecated Use `icon` with the default `iconPlacement="left"` instead. */
  leftIcon?: IconName;
  /** @deprecated Use `icon` with `iconPlacement="right"` instead. */
  rightIcon?: IconName;
  // Most button labels are strings, but a few V2 triggers need small
  // inline indicators such as a live dot while preserving canonical button chrome.
  children?: React.ReactNode;
}

export type ButtonV2Props = SharedProps & Omit<React.ComponentProps<"button">, "ref" | "children">;

const resolveButtonV2IconProps = ({
  icon,
  iconPlacement,
  leftIcon,
  rightIcon,
}: Pick<SharedProps, "icon" | "iconPlacement" | "leftIcon" | "rightIcon">): {
  icon: IconName | undefined;
  iconPlacement: ButtonV2IconPlacement;
} => {
  if (icon != null) {
    return {
      icon,
      iconPlacement: iconPlacement ?? "left",
    };
  }

  if (rightIcon != null) {
    return {
      icon: rightIcon,
      iconPlacement: iconPlacement ?? "right",
    };
  }

  return {
    icon: leftIcon,
    iconPlacement: iconPlacement ?? "left",
  };
};

const ButtonV2Base = forwardRef(function ButtonV2Impl(
  {
    type = "button",
    variant = "secondary",
    size = "md",
    loading = false,
    disabled,
    icon,
    iconPlacement,
    leftIcon,
    rightIcon,
    children,
    className,
    onClick,
    "data-testid": dataTestId,
    ...rest
  }: ButtonV2Props,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const resolvedIconProps = resolveButtonV2IconProps({
    icon,
    iconPlacement,
    leftIcon,
    rightIcon,
  });
  const isIconOnly = children == null && (loading || resolvedIconProps.icon != null);
  const amplitudeOnClick = useAmplitudeOnClick({
    event: AmplitudeEvent.DESIGN_SYSTEM_BUTTON_CLICK,
    component: "ButtonV2",
    text: typeof children === "string" ? children : undefined,
    extraProperties: {
      color: variant,
      icon: resolvedIconProps.icon,
      data_test_id: dataTestId,
    },
  });

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      data-testid={dataTestId}
      className={classNames(
        baseClasses,
        isIconOnly ? iconOnlyButtonSizeClasses[size] : sizeClasses[size],
        variantClasses[variant],
        className,
      )}
      {...rest}
      onClick={(event) => {
        amplitudeOnClick();
        onClick?.(event);
      }}
    >
      <ButtonV2Content loading={loading} iconSize={size} {...resolvedIconProps}>
        {children}
      </ButtonV2Content>
    </button>
  );
});

export const ButtonV2 = React.memo(ButtonV2Base);
ButtonV2.displayName = "ButtonV2";

export interface LinkButtonV2Props
  extends SharedProps,
    Omit<React.ComponentProps<"a">, "ref" | "children"> {
  href: string;
  disabled?: boolean;
  openInNewTab?: boolean;
}

const LinkButtonV2Base = forwardRef(function LinkButtonV2Impl(
  {
    variant = "secondary",
    size = "md",
    loading = false,
    disabled,
    icon,
    iconPlacement,
    leftIcon,
    rightIcon,
    href,
    children,
    className,
    onClick,
    "data-testid": dataTestId,
    ...rest
  }: LinkButtonV2Props,
  ref: ForwardedRef<HTMLAnchorElement>,
) {
  const isInert = disabled || loading;
  const resolvedIconProps = resolveButtonV2IconProps({
    icon,
    iconPlacement,
    leftIcon,
    rightIcon,
  });
  const isIconOnly = children == null && (loading || resolvedIconProps.icon != null);
  const amplitudeOnClick = useAmplitudeOnClick({
    event: AmplitudeEvent.DESIGN_SYSTEM_BUTTON_CLICK,
    component: "LinkButtonV2",
    href,
    text: typeof children === "string" ? children : undefined,
    extraProperties: {
      color: variant,
      icon: resolvedIconProps.icon,
      data_test_id: dataTestId,
    },
  });

  return (
    <ChalkLink
      unstyled
      ref={ref}
      href={href}
      data-testid={dataTestId}
      aria-disabled={isInert || undefined}
      tabIndex={isInert ? -1 : undefined}
      className={classNames(
        baseClasses,
        isIconOnly ? iconOnlyButtonSizeClasses[size] : sizeClasses[size],
        variantClasses[variant],
        isInert && "pointer-events-none",
        className,
      )}
      {...rest}
      onClick={(event) => {
        if (isInert) {
          event.preventDefault();
          return;
        }
        amplitudeOnClick();
        onClick?.(event);
      }}
    >
      <ButtonV2Content loading={loading} iconSize={size} {...resolvedIconProps}>
        {children}
      </ButtonV2Content>
    </ChalkLink>
  );
});

export const LinkButtonV2 = React.memo(LinkButtonV2Base);
LinkButtonV2.displayName = "LinkButtonV2";

const ButtonV2Content: React.FC<{
  loading: boolean;
  icon?: IconName;
  iconPlacement: ButtonV2IconPlacement;
  iconSize: ButtonV2Size;
  children?: React.ReactNode;
}> = ({ loading, icon, iconPlacement, iconSize, children }) => {
  const sizeKey = buttonIconSize[iconSize];
  // The spinner is a fixed Lucide glyph (not part of IconName) — sized
  // to match the variant's icon slot using IconSizes directly.
  const spinner = (
    <Loader2 className={classNames(IconSizes[sizeKey], "animate-spin")} strokeWidth={1.75} />
  );

  return (
    <>
      {loading ? spinner : iconPlacement === "left" && icon && <Icon name={icon} size={sizeKey} />}
      {children}
      {!loading && iconPlacement === "right" && icon && <Icon name={icon} size={sizeKey} />}
    </>
  );
};

// ------------------------------------------------------------------
// IconButtonV2 — square icon-only button.
//
// Width matches the ButtonV2 height for each size (24/28/32/36 px).
// Variants:
//   - secondary       bordered surface (like ButtonV2 secondary)
//   - tertiary        same fill as secondary, no visible stroke
//   - danger-subdued  tertiary chrome, icon turns red on hover/active
// Shares baseClasses + disabledClasses + icon sizing with ButtonV2; the
// fills and strokes diverge in dark mode (one step softer than the text
// button, so the default melts into the canvas).
// ------------------------------------------------------------------

export const IconButtonV2Variants = ["secondary", "tertiary", "danger-subdued"] as const;
export type IconButtonV2Variant = (typeof IconButtonV2Variants)[number];

const iconButtonSizeClasses: Record<IconButtonV2Size, string> = {
  // 3xs for the inline case
  "3xs": "h-4 w-4 shrink-0 rounded-cds-6",
  "2xs": "h-5 w-5 shrink-0 rounded-cds-6",
  xs: "h-6 w-6 shrink-0 rounded-cds-6",
  sm: "h-7 w-7 shrink-0 rounded-cds-6",
  md: "h-8 w-8 shrink-0 rounded-cds-6",
  lg: "h-9 w-9 shrink-0 rounded-cds-6",
};

// Icon's own scale, for reference: xs 10px, sm 12px, md 14px, lg 20px. Sizes below are stated from
// that scale -- they previously claimed 16px for md and lg, which Icon has no step for.
const iconButtonIconSize: Record<IconButtonV2Size, IconSize> = {
  "3xs": "xs", // 10px inside a 16px square
  "2xs": "xs", // 10px inside a 20px square
  xs: "sm", //   12px inside a 24px square
  sm: "sm", //   12px inside a 28px square
  md: "md", //   14px inside a 32px square
  lg: "md", //   14px inside a 36px square
};

const iconButtonVariantClasses: Record<IconButtonV2Variant, string> = {
  secondary: classNames(
    // Light — icon buttons sit on light rails/canvases, so hover needs
    // enough contrast to read over both zinc-50 and zinc-100 chrome.
    "bg-cds-surface-default text-cds-ink border-[1px] border-cds-surface-border",
    "hover:bg-cds-surface-border",
    "aria-expanded:bg-cds-surface-border",
    "active:bg-cds-surface-border",
    // Dark — fills shifted one step down (transparent / 4% / 8% vs
    // ButtonV2's 4% / 8% / 12%). Default transparent so the button
    // disappears into the canvas; strokes and shadows track ButtonV2.
    "dark:bg-transparent dark:text-zinc-400 dark:border-white/10",
    "dark:hover:bg-white/[0.04] dark:hover:border-white/[0.16]",
    "dark:hover:text-zinc-50",
    "dark:aria-expanded:bg-white/[0.04] dark:aria-expanded:border-white/[0.16]",
    "dark:aria-expanded:text-zinc-50",
    "dark:active:bg-white/[0.08] dark:active:border-white/10",
    "dark:active:text-zinc-50",
    // Icon palette — light mirrors primary (icons inherit text
    // color). Dark starts muted and illuminates on whole-button hover.
    "dark:[&_svg]:!text-zinc-400",
    "dark:[&:hover_svg]:!text-zinc-50 dark:aria-expanded:[&_svg]:!text-zinc-50 dark:[&:active_svg]:!text-zinc-50",
    "dark:disabled:[&_svg]:!text-zinc-100",
    // Elevation — shadow-cds-s default/hover, inset on active (light only).
    // The `disabled:!shadow-none` here is redundant with baseClasses but
    // makes the intent visible next to the shadow rules it overrides.
    "shadow-cds-s active:shadow-cds-inset disabled:!shadow-none",
    "dark:shadow-none dark:active:shadow-none",
    "disabled:!bg-cds-surface-active dark:disabled:!bg-white/[0.04]",
    "disabled:!border-cds-surface-border dark:disabled:!border-white/10",
  ),
  // Tertiary = secondary without a visible stroke. Disabled/loading
  // mirror the default look (no chrome, default text color) and use
  // opacity-50 to signal inactivity.
  tertiary: classNames(
    "bg-transparent text-cds-ink-subtle border-[1px] border-transparent",
    "hover:bg-cds-surface-border hover:text-cds-ink-strong",
    "aria-expanded:bg-cds-surface-border aria-expanded:text-cds-ink-strong",
    "active:bg-cds-surface-border active:text-cds-ink-strong",
    "[&_svg]:!text-cds-ink-subtle",
    "[&:hover_svg]:!text-cds-ink-strong aria-expanded:[&_svg]:!text-cds-ink-strong [&:active_svg]:!text-cds-ink-strong",
    "dark:bg-transparent dark:text-zinc-400",
    "dark:hover:bg-white/[0.04]",
    "dark:hover:text-zinc-50",
    "dark:aria-expanded:bg-white/[0.04] dark:aria-expanded:text-zinc-50",
    "dark:active:bg-white/[0.08]",
    "dark:active:text-zinc-50",
    // Dark starts muted and illuminates on whole-button hover.
    "dark:[&_svg]:!text-zinc-400",
    "dark:[&:hover_svg]:!text-zinc-50 dark:aria-expanded:[&_svg]:!text-zinc-50 dark:[&:active_svg]:!text-zinc-50",
    "dark:disabled:[&_svg]:!text-zinc-100",
    // Disabled — transparent fill + border in both modes, default text.
    "disabled:!bg-transparent dark:disabled:!bg-transparent",
    "disabled:!border-transparent dark:disabled:!border-transparent",
    "disabled:!text-cds-ink",
    "disabled:opacity-50",
  ),
  // Danger-subdued: tertiary-like chrome, red intent on interaction. The
  // icon color carries the destructive signal — neutral until the user
  // shows intent. Common use: row-level "× remove" controls where fully
  // red chrome repeated many times would feel loud.
  "danger-subdued": classNames(
    "bg-cds-surface-default text-cds-ink border-[1px] border-transparent",
    "hover:bg-red-50 hover:text-red-600",
    "aria-expanded:bg-red-50 aria-expanded:text-red-600",
    "active:bg-red-100 active:text-red-700",
    "dark:bg-transparent dark:text-zinc-100",
    "dark:hover:bg-red-500/10 dark:hover:text-red-400",
    "dark:aria-expanded:bg-red-500/10 dark:aria-expanded:text-red-400",
    "dark:active:bg-red-500/20 dark:active:text-red-500",
    // Disabled mirrors default (same pattern as tertiary).
    "disabled:!bg-transparent dark:disabled:!bg-transparent",
    "disabled:!border-transparent dark:disabled:!border-transparent",
    "disabled:!text-cds-ink",
    "disabled:opacity-50",
  ),
};

const iconButtonDisabledVariantClasses: Record<IconButtonV2Variant, string> = {
  secondary: classNames(
    "bg-cds-surface-active text-cds-ink-muted border-[1px] border-cds-surface-border",
    "[&_svg]:!text-cds-ink-muted",
    "dark:bg-white/[0.04] dark:text-zinc-400 dark:border-white/10",
    "dark:[&_svg]:!text-zinc-400",
    "shadow-none",
  ),
  tertiary: classNames(
    "bg-transparent text-cds-ink-subtle border-[1px] border-transparent",
    "[&_svg]:!text-cds-ink-subtle",
    "dark:bg-transparent dark:text-zinc-400 dark:border-transparent",
    "dark:[&_svg]:!text-zinc-400",
    "opacity-50 shadow-none",
  ),
  "danger-subdued": classNames(
    "bg-transparent text-cds-ink border-[1px] border-transparent",
    "[&_svg]:!text-cds-ink",
    "dark:bg-transparent dark:text-zinc-100 dark:border-transparent",
    "dark:[&_svg]:!text-zinc-100",
    "opacity-50 shadow-none",
  ),
};

const iconButtonDisabledIconClasses: Record<IconButtonV2Variant, string> = {
  secondary: "!text-cds-ink-muted dark:!text-zinc-400",
  tertiary: "!text-cds-ink-subtle dark:!text-zinc-400",
  "danger-subdued": "!text-cds-ink dark:!text-zinc-100",
};

export interface IconButtonV2Props
  extends Omit<React.ComponentProps<"button">, "ref" | "children" | "aria-label"> {
  "data-testid"?: string;
  variant?: IconButtonV2Variant;
  size?: IconButtonV2Size;
  iconSize?: IconSize;
  loading?: boolean;
  icon: IconName;
  tooltipText?: string;
  tooltipPlacement?: TooltipPlacement;
  tooltipInstant?: boolean;
  /** Required — icon-only buttons have no readable text for screen readers. */
  "aria-label": string;
}

const IconButtonV2Base = forwardRef(function IconButtonV2Impl(
  {
    type = "button",
    variant = "secondary",
    size = "md",
    iconSize,
    loading = false,
    disabled,
    icon,
    tooltipText,
    tooltipPlacement,
    tooltipInstant,
    className,
    onClick,
    "data-testid": dataTestId,
    ...rest
  }: IconButtonV2Props,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const sizeKey = iconSize ?? iconButtonIconSize[size];
  const isDisabled = disabled || loading;
  const amplitudeOnClick = useAmplitudeOnClick({
    event: AmplitudeEvent.DESIGN_SYSTEM_BUTTON_CLICK,
    component: "IconButtonV2",
    text: rest["aria-label"],
    extraProperties: {
      color: variant,
      icon,
      data_test_id: dataTestId,
    },
  });
  const button = (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      data-testid={dataTestId}
      className={classNames(
        baseClasses,
        iconButtonSizeClasses[size],
        isDisabled ? iconButtonDisabledVariantClasses[variant] : iconButtonVariantClasses[variant],
        className,
      )}
      {...rest}
      onClick={(event) => {
        amplitudeOnClick();
        onClick?.(event);
      }}
    >
      {loading ? (
        <Loader2
          className={classNames(
            IconSizes[sizeKey],
            "animate-spin",
            iconButtonDisabledIconClasses[variant],
          )}
          strokeWidth={1.75}
        />
      ) : (
        <Icon
          name={icon}
          size={sizeKey}
          className={isDisabled ? iconButtonDisabledIconClasses[variant] : undefined}
        />
      )}
    </button>
  );

  if (tooltipText == null || tooltipText === "") {
    return button;
  }

  const tooltipTrigger = isDisabled ? <span className="inline-flex">{button}</span> : button;
  return (
    <TooltipV2 content={tooltipText} placement={tooltipPlacement} instant={tooltipInstant}>
      {tooltipTrigger}
    </TooltipV2>
  );
});

export const IconButtonV2 = React.memo(IconButtonV2Base);
IconButtonV2.displayName = "IconButtonV2";

export interface IconLinkButtonV2Props
  extends Omit<React.ComponentProps<"a">, "ref" | "children" | "aria-label" | "href"> {
  "data-testid"?: string;
  variant?: IconButtonV2Variant;
  size?: IconButtonV2Size;
  iconSize?: IconSize;
  icon: IconName;
  tooltipText?: string;
  tooltipPlacement?: TooltipPlacement;
  tooltipInstant?: boolean;
  /** Required — icon-only links have no readable text for screen readers. */
  "aria-label": string;
  href: string;
}

const IconLinkButtonV2Base = forwardRef(function IconLinkButtonV2Impl(
  {
    variant = "secondary",
    size = "md",
    iconSize,
    icon,
    href,
    tooltipText,
    tooltipPlacement,
    tooltipInstant,
    className,
    onClick,
    target,
    "data-testid": dataTestId,
    ...rest
  }: IconLinkButtonV2Props,
  ref: ForwardedRef<HTMLAnchorElement>,
) {
  const sizeKey = iconSize ?? iconButtonIconSize[size];
  const amplitudeOnClick = useAmplitudeOnClick({
    event: AmplitudeEvent.DESIGN_SYSTEM_BUTTON_CLICK,
    component: "IconLinkButtonV2",
    href,
    text: rest["aria-label"],
    extraProperties: {
      color: variant,
      icon,
      data_test_id: dataTestId,
    },
  });
  const link = (
    <ChalkLink
      unstyled
      ref={ref}
      href={href}
      openInNewTab={target === "_blank"}
      showOpenInNewTabIcon={false}
      data-testid={dataTestId}
      className={classNames(
        baseClasses,
        iconButtonSizeClasses[size],
        iconButtonVariantClasses[variant],
        className,
      )}
      {...rest}
      onClick={(event) => {
        amplitudeOnClick();
        onClick?.(event);
      }}
    >
      <Icon name={icon} size={sizeKey} />
    </ChalkLink>
  );

  if (tooltipText == null || tooltipText === "") {
    return link;
  }

  return (
    <TooltipV2 content={tooltipText} placement={tooltipPlacement} instant={tooltipInstant}>
      {link}
    </TooltipV2>
  );
});

export const IconLinkButtonV2 = React.memo(IconLinkButtonV2Base);
IconLinkButtonV2.displayName = "IconLinkButtonV2";
